import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger
} from '@heroui/react';
import {
  ArrowRight,
  FastArrowUpSquare,
  MoreVert,
  Reply,
  SendDiagonal,
  Trash,
  OpenNewWindow
} from 'iconoir-react';

import capitalizeFirstLetter from '~/common/helpers/capitalizeFirstLetter';
import featureFlagConstants from '~/common/helpers/featureFlagConstants';
import { getFeatureFlag } from '~/common/helpers/globalFeatureFlags';
import i18n from '~/common/helpers/i18n';
import Role from '~/components/Role';

export type DeploymentActionsMenuProps = {
  onPromote: () => void;
  onRedeploy: () => void;
  onRollback: () => void;
  onDeleteDeploy: () => void;
  onGoToPipeline: () => void;
  onViewExecutions: () => void;
  onOpenVersionDiff: () => void;
  isDisabled?: boolean;
  showPromote?: boolean;
  environment?: string;
  testIdPrefix?: string;
  pipelineVersionMajor?: number;
  pipelineVersionMinor?: number;
};

export function DeploymentActionsMenu({
  onPromote,
  onRedeploy,
  onRollback,
  onDeleteDeploy,
  onGoToPipeline,
  onViewExecutions,
  onOpenVersionDiff,
  isDisabled = false,
  showPromote = false,
  environment,
  testIdPrefix,
  pipelineVersionMajor,
  pipelineVersionMinor
}: DeploymentActionsMenuProps) {
  const isDesignTimePipelineDiffEnabled = getFeatureFlag(
    featureFlagConstants.DESIGN_TIME_PIPELINE_DIFF
  ) as boolean;
  const showVersionDiff =
    isDesignTimePipelineDiffEnabled &&
    pipelineVersionMajor !== undefined &&
    pipelineVersionMinor !== undefined &&
    (pipelineVersionMajor > 1 ||
      (pipelineVersionMajor === 1 && pipelineVersionMinor > 0));

  return (
    <Role
      name={[
        `DEPLOYMENT:DELETE{ENV=${environment?.toUpperCase()}}`,
        'DEPLOYMENT:DELETE'
      ]}
    >
      <Dropdown
        className='rounded-md bg-white shadow-xl'
        placement='bottom-end'
      >
        <DropdownTrigger>
          <Button
            data-testid={`${testIdPrefix}-actions-menu`}
            aria-label='Deployment actions'
            isIconOnly
            variant='light'
            className='aria-[expanded=true]:bg-surface-container-low'
            size='sm'
            isDisabled={isDisabled}
          >
            <MoreVert className='icon-small' />
          </Button>
        </DropdownTrigger>
        <DropdownMenu
          classNames={{
            base: '!text-label-lg text-on-surface-variant font-semibold'
          }}
        >
          {showPromote ? (
            <DropdownSection
              classNames={{
                divider: 'bg-outline'
              }}
              showDivider
            >
              <DropdownItem
                key='promote'
                data-testid={`${testIdPrefix}-promote`}
                startContent={<FastArrowUpSquare className='icon-small' />}
                onPress={onPromote}
                className='p-2'
              >
                {capitalizeFirstLetter(i18n.t('action.promote').toLowerCase())}
              </DropdownItem>
            </DropdownSection>
          ) : null}
          <DropdownSection
            classNames={{
              divider: 'bg-outline'
            }}
            showDivider
          >
            <DropdownItem
              key='redeploy'
              data-testid={`${testIdPrefix}-redeploy`}
              startContent={<SendDiagonal className='icon-small' />}
              onPress={onRedeploy}
              className='p-2'
            >
              {i18n.t('scenes.runtime.labels.redeploy')}
            </DropdownItem>
            <DropdownItem
              key='rollback'
              data-testid={`${testIdPrefix}-rollback`}
              startContent={<Reply className='icon-small' />}
              onPress={onRollback}
              className='p-2'
            >
              Rollback
            </DropdownItem>
            <DropdownItem
              key='delete'
              data-testid={`${testIdPrefix}-delete`}
              startContent={<Trash className='icon-small' />}
              onPress={onDeleteDeploy}
              className='p-2'
            >
              {i18n.t('scenes.runtime.actions.remove_deploy')}
            </DropdownItem>
          </DropdownSection>
          <DropdownItem
            key='go-to-pipeline'
            data-testid={`${testIdPrefix}-open-canvas`}
            onPress={onGoToPipeline}
            endContent={<ArrowRight className='icon-smallest' />}
            className='p-2'
          >
            {i18n.t('scenes.runtime.actions.open_canvas')}
          </DropdownItem>
          <DropdownSection
            classNames={{
              divider: 'bg-outline'
            }}
            showDivider={showVersionDiff}
          >
            <DropdownItem
              key='view-executions'
              data-testid={`${testIdPrefix}-view-executions`}
              onPress={onViewExecutions}
              endContent={<ArrowRight className='icon-smallest' />}
              className='p-2'
            >
              {i18n.t('scenes.runtime.actions.view_executions')}
            </DropdownItem>
          </DropdownSection>
          {showVersionDiff ? (
            <DropdownItem
              key='view-version-diff'
              data-testid={`${testIdPrefix}-view-version-diff`}
              onPress={onOpenVersionDiff}
              endContent={<OpenNewWindow className='icon-smallest' />}
              className='p-2'
            >
              {i18n.t('label.open_version_diff')}
            </DropdownItem>
          ) : null}
        </DropdownMenu>
      </Dropdown>
    </Role>
  );
}
